
import React from 'react';
import { User, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from '@/components/ui/card';
import { TripMember } from '@/hooks/travel/useTripMembers';

interface TravelerCardProps {
  member: TripMember;
  currentUserId?: string;
}

export const TravelerCard: React.FC<TravelerCardProps> = ({ member, currentUserId }) => {
  // Get initials from name or username
  const getInitials = (member: TripMember): string => {
    const name = member.profiles?.username || '';
    if (!name) return '?';
    
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Card className="overflow-hidden border bg-card hover:shadow-md transition-shadow">
      <div className="flex items-start p-4">
        <Avatar className="h-12 w-12 mr-4">
          <AvatarImage 
            src={member.profiles?.avatar_url || ''} 
            alt={member.profiles?.username || 'User'} 
          />
          <AvatarFallback className="bg-purple-100 text-purple-600">
            {getInitials(member)}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-medium">
                {member.profiles?.username || 'Unnamed User'}
              </h3>
              <p className="text-sm text-muted-foreground flex items-center mt-1">
                <User className="h-3.5 w-3.5 mr-1.5" />
                {member.profiles?.username || 'No username'}
              </p>
            </div>
            
            {member.user_id === currentUserId && (
              <Badge className="ml-2 bg-blue-100 text-blue-700 hover:bg-blue-200">
                You
              </Badge>
            )}
          </div>
          
          {member.profiles?.email && (
            <p className="text-sm text-muted-foreground flex items-center mt-2">
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              {member.profiles.email}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};
